import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import type { AkvaryumEntryDto } from "@sikilesi/protocol";
import { Link } from "../components/Link";
import { useServices } from "../components/Services";
import { FISH_IMG } from "../lib/gol";
import { up } from "../lib/tr";

const when = (iso: string) =>
  new Date(iso).toLocaleString("tr-TR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" });

/** /akvaryum — every catch, newest first, one page at a time (no infinite scroll). */
export function AkvaryumPage(): ReactElement {
  const { api } = useServices();
  const [items, setItems] = useState<AkvaryumEntryDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");
  const started = useRef(false);

  const load = useCallback(
    (from: string | null) => {
      setBusy(true);
      api.akvaryumPage(from, 20).then(
        (page) => {
          setItems((old) => [...old, ...page.items.filter((x) => !old.some((o) => o.id === x.id))]);
          setCursor(page.next_cursor);
          setLoaded(true);
          setBusy(false);
          if (from) setAnnounce(`${page.items.length} kayıt daha yüklendi.`);
        },
        () => {
          setError("Akvaryum okunamadı.");
          setBusy(false);
        },
      );
    },
    [api],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    document.title = "Akvaryum · sikile.si";
    load(null);
  }, [load]);

  return (
    <section className="z-akvpage" aria-labelledby="z-akvpage-title">
      <Link href="/" className="z-back">
        ← {up("akışa dön")}
      </Link>
      <div className="z-clip z-akvpage__clip">
        <span className="z-tape z-tape--tl" aria-hidden="true" />
        <h1 id="z-akvpage-title" className="z-akvpage__title">
          {up("tüm akvaryum")}
        </h1>
        <p className="z-small">
          Sitenin sahte kapılarını yoklayan botlar. Kimlik yok: her biri bağlantısının geri çevrilemez bir özetinden adlandırılır, ham IP saklanmaz.
          Tuzağı fark edip ihbar edenler şeref listesine yazılır.
        </p>
        {error ? <p role="alert">{error}</p> : null}
        {!loaded && !error ? <p className="z-mono">Akvaryum dolduruluyor…</p> : null}
        {loaded && items.length === 0 ? <p className="z-mono">Göl sakin. Henüz kimse yemi yoklamadı.</p> : null}
        <ol className="z-akvpage__list">
          {items.map((e) => {
            const img = FISH_IMG[e.species];
            return (
              <li key={e.id} className={`z-akvpage__row is-${e.species}`}>
                <img className="z-akv__icon" src={img.src} width={img.w} height={img.h} alt="" loading="lazy" decoding="async" />
                <p className="z-akv__text">
                  <strong>{e.name}</strong> — {e.line}
                  {e.times > 1 && e.species !== "turna" ? ` ${e.times}. kez yakalandı.` : ""}
                </p>
                <time className="z-akvpage__when" dateTime={e.at}>
                  {when(e.at)}
                </time>
              </li>
            );
          })}
        </ol>
        <p className="z-sr" role="status" aria-live="polite">
          {announce}
        </p>
        {cursor ? (
          <button
            type="button"
            className="z-btn z-akvpage__more"
            disabled={busy}
            onClick={() => {
              load(cursor);
            }}
          >
            {up(busy ? "yükleniyor" : "daha eski balıklar")}
          </button>
        ) : loaded && items.length > 0 ? (
          <p className="z-akvpage__end">{up("gölün dibi")}</p>
        ) : null}
      </div>
    </section>
  );
}
